import { useEffect, useState } from 'react';
import { API_BASE } from './api-base';
import { PROFILE_LINKS_SPEC, toggleOverride } from './dev-feature-toggles';

/**
 * Client read of Symphony Profile Links — `symphonyProfileLinks`.
 *
 * FAILS CLOSED to control (today's app) until the gate answers, and on any error. **It hides screens; it
 * protects nothing**: the server routes behind the feature conduct the same release themselves
 * (`lib/profile-links-flag-server.ts`, and the VMR recipient service for a handle claim).
 *
 * A dev-tools Feature toggles override wins over the conducted value, and is inert without a dev-tools grant.
 */
const GATE_TIMEOUT_MS = 1500;

let cached: Promise<boolean> | null = null;

export function profileLinksEnabled(): Promise<boolean> {
  // Checked on every call, not memoized: the override can be set mid-session from the dev panel.
  const forced = toggleOverride(PROFILE_LINKS_SPEC);
  if (forced !== undefined) return Promise.resolve(forced === 'true');
  if (cached) return cached;
  cached = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/flags/profile-links`, {
        credentials: 'include',
        signal: AbortSignal.timeout(GATE_TIMEOUT_MS),
      });
      if (!res.ok) return false;
      return ((await res.json()) as boolean) === true;
    } catch {
      return false;
    }
  })();
  return cached;
}

/** The arm, for rendering. Starts false, so a user outside the release never sees a profile surface flash in. */
export function useProfileLinksEnabled(): boolean {
  return useProfileLinksGate() === true;
}

/** The arm, or `undefined` until the gate has answered — for a caller that must tell "off" from "not known yet". */
export function useProfileLinksGate(): boolean | undefined {
  const [enabled, setEnabled] = useState<boolean | undefined>(undefined);
  useEffect(() => {
    let live = true;
    void profileLinksEnabled().then((on) => {
      if (live) setEnabled(on);
    });
    return () => {
      live = false;
    };
  }, []);
  return enabled;
}
